// Shared Bible Constants
// Used across the application for consistent book naming and metadata

// OSIS book abbreviation to full name mapping
export const OSIS_BOOK_NAMES = {
  Gen: 'Genesis',
  Exod: 'Exodus',
  Lev: 'Leviticus',
  Num: 'Numbers',
  Deut: 'Deuteronomy',
  Josh: 'Joshua',
  Judg: 'Judges',
  Ruth: 'Ruth',
  '1Sam': '1 Samuel',
  '2Sam': '2 Samuel',
  '1Kgs': '1 Kings',
  '2Kgs': '2 Kings',
  '1Chr': '1 Chronicles',
  '2Chr': '2 Chronicles',
  Ezra: 'Ezra',
  Neh: 'Nehemiah',
  Esth: 'Esther',
  Job: 'Job',
  Ps: 'Psalms',
  Prov: 'Proverbs',
  Eccl: 'Ecclesiastes',
  Song: 'Song of Solomon',
  Isa: 'Isaiah',
  Jer: 'Jeremiah',
  Lam: 'Lamentations',
  Ezek: 'Ezekiel',
  Dan: 'Daniel',
  Hos: 'Hosea',
  Joel: 'Joel',
  Amos: 'Amos',
  Obad: 'Obadiah',
  Jonah: 'Jonah',
  Mic: 'Micah',
  Nah: 'Nahum',
  Hab: 'Habakkuk',
  Zeph: 'Zephaniah',
  Hag: 'Haggai',
  Zech: 'Zechariah',
  Mal: 'Malachi',
  Matt: 'Matthew',
  Mark: 'Mark',
  Luke: 'Luke',
  John: 'John',
  Acts: 'Acts',
  Rom: 'Romans',
  '1Cor': '1 Corinthians',
  '2Cor': '2 Corinthians',
  Gal: 'Galatians',
  Eph: 'Ephesians',
  Phil: 'Philippians',
  Col: 'Colossians',
  '1Thess': '1 Thessalonians',
  '2Thess': '2 Thessalonians',
  '1Tim': '1 Timothy',
  '2Tim': '2 Timothy',
  Titus: 'Titus',
  Phlm: 'Philemon',
  Heb: 'Hebrews',
  Jas: 'James',
  '1Pet': '1 Peter',
  '2Pet': '2 Peter',
  '1John': '1 John',
  '2John': '2 John',
  '3John': '3 John',
  Jude: 'Jude',
  Rev: 'Revelation'
};

// Chapter counts for each book (66 books total)
export const CHAPTER_COUNTS = {
  Gen: 50,
  Exod: 40,
  Lev: 27,
  Num: 36,
  Deut: 34,
  Josh: 24,
  Judg: 21,
  Ruth: 4,
  '1Sam': 31,
  '2Sam': 24,
  '1Kgs': 22,
  '2Kgs': 25,
  '1Chr': 29,
  '2Chr': 36,
  Ezra: 10,
  Neh: 13,
  Esth: 10,
  Job: 42,
  Ps: 150,
  Prov: 31,
  Eccl: 12,
  Song: 8,
  Isa: 66,
  Jer: 52,
  Lam: 5,
  Ezek: 48,
  Dan: 12,
  Hos: 14,
  Joel: 3,
  Amos: 9,
  Obad: 1,
  Jonah: 4,
  Mic: 7,
  Nah: 3,
  Hab: 3,
  Zeph: 3,
  Hag: 2,
  Zech: 14,
  Mal: 4,
  Matt: 28,
  Mark: 16,
  Luke: 24,
  John: 21,
  Acts: 28,
  Rom: 16,
  '1Cor': 16,
  '2Cor': 13,
  Gal: 6,
  Eph: 6,
  Phil: 4,
  Col: 4,
  '1Thess': 5,
  '2Thess': 3,
  '1Tim': 6,
  '2Tim': 4,
  Titus: 3,
  Phlm: 1,
  Heb: 13,
  Jas: 5,
  '1Pet': 5,
  '2Pet': 3,
  '1John': 5,
  '2John': 1,
  '3John': 1,
  Jude: 1,
  Rev: 22
};

// Helper function to get book name from OSIS ID
export function getBookName(osisId) {
  return OSIS_BOOK_NAMES[osisId] || osisId;
}

// Helper function to get chapter count
export function getChapterCount(osisId) {
  return CHAPTER_COUNTS[osisId] || 1;
}

export function bookNameToSlug(name) {
  return name
    .toLowerCase()
    .replace(/'/g, '')
    .replace(/\s+/g, '-');
}

export const OSIS_TO_SLUG = Object.fromEntries(
  Object.entries(OSIS_BOOK_NAMES).map(([osisId, name]) => [osisId, bookNameToSlug(name)])
);

export const SLUG_TO_OSIS = Object.fromEntries(
  Object.entries(OSIS_TO_SLUG).map(([osisId, slug]) => [slug, osisId])
);

export function slugToOsis(slug) {
  if (!slug) return null;
  return SLUG_TO_OSIS[slug.toLowerCase()] || null;
}

/**
 * Parse /reader/... or /bible/... paths into navigation targets.
 * Returns null when the path does not specify a book/chapter.
 */
export function parseNavigationFromPath(pathname) {
  const readerMatch = pathname.match(/^\/reader\/([^/]+)(?:\/(\d+))?(?:\/([^/]+))?/);
  if (readerMatch) {
    return {
      bookId: readerMatch[1],
      chapter: parseInt(readerMatch[2] || '1', 10),
      verseId: readerMatch[3] || null,
    };
  }

  const bibleMatch = pathname.match(/^\/bible\/([^/]+)\/(\d+)(?:\/([^/]+))?/);
  if (bibleMatch) {
    const bookId = slugToOsis(bibleMatch[1]);
    if (!bookId) return null;
    return {
      bookId,
      chapter: parseInt(bibleMatch[2], 10),
      verseId: bibleMatch[3] || null,
    };
  }

  return null;
}

export function isResumeReadingPath(pathname) {
  return pathname === '/' || pathname === '/reader';
}

export const SITE_URL = 'https://truthbound.app';

/**
 * Public share URL for a verse — points at the crawlable /bible/ static page.
 */
export function getBibleShareUrl(osisId, chapter, verse) {
  const slug = OSIS_TO_SLUG[osisId];
  const chapterNum = parseInt(chapter, 10);
  if (!slug || !chapterNum) {
    return `${SITE_URL}/reader/${osisId}/${chapter}/${verse}`;
  }

  const verseStr = String(verse);
  const verseNum = verseStr.includes('-') ? verseStr.split('-')[0] : verseStr;
  return `${SITE_URL}/bible/${slug}/${chapterNum}/${verseNum}`;
}
